import { useState } from 'react';
import type { Contract } from '@/features/admin/contracts/types';
import type { Discount, DiscountFormData, UseDiscountsReturn } from '../types/discount';
import { useCreateDiscount } from './use-create-discount';
import { useUpdateDiscount } from './use-update-discount';
import { useDeleteDiscount } from './use-delete-discount';
import { getContract } from '@/lib/coloc-api/admin';
import { showToast } from '@/services/toast-service';

const convertApiDiscountToDiscount = (apiDiscount: any): Discount => ({
  id: apiDiscount.id,
  amount_cents: apiDiscount.amount_cents,
  measurement: apiDiscount.measurement || 'absolute',
  period: apiDiscount.period || 'this_month',
  note: apiDiscount.note,
  start_date: apiDiscount.start_date,
  end_date: apiDiscount.end_date,
  attachments: apiDiscount.attachments,
  discount_category: apiDiscount.discount_category,
  date: apiDiscount.date || null,
});

export const useDiscounts = (contract: Contract): UseDiscountsReturn => {
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState<Discount | null>(null);
  const [showDiscountInfoModal, setShowDiscountInfoModal] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState<Discount | null>(null);
  const [showEditDiscountModal, setShowEditDiscountModal] = useState(false);
  const [discounts, setDiscounts] = useState<Discount[]>(
    (contract.discounts || []).map(convertApiDiscountToDiscount)
  );

  const refreshDiscounts = async () => {
    try {
      const contractResponse = await getContract({ id: contract.id });
      if (contractResponse.data) {
        setDiscounts((contractResponse.data.discounts || []).map(convertApiDiscountToDiscount));
      }
    } catch (error) {
      showToast.error({ text: 'Failed to fetch updated contract data.' });
    }
  };

  const { createDiscount } = useCreateDiscount(contract.id, refreshDiscounts);
  const { updateDiscount } = useUpdateDiscount(contract.id, refreshDiscounts);
  const { deleteDiscount } = useDeleteDiscount(contract.id, refreshDiscounts);

  const handleAddDiscount = () => {
    setEditingDiscount(null);
    setShowDiscountModal(true);
  };

  const handleEditDiscount = (discount: Discount) => {
    setEditingDiscount(discount);
    setShowEditDiscountModal(true);
  };

  const handleDeleteDiscount = async (discountId: number) => {
    await deleteDiscount(discountId);
  };

  const handleSubmitDiscount = async (data: DiscountFormData) => {
    const result = await createDiscount(data);
    setEditingDiscount(null);
    return result;
  };

  const handleSubmitEditDiscount = async (data: DiscountFormData) => {
    if (!editingDiscount || !editingDiscount.id) {
      return;
    }
    await updateDiscount(editingDiscount.id, data);
    setShowEditDiscountModal(false);
    setEditingDiscount(null);
  };

  const handleViewDiscount = (discount: Discount) => {
    setSelectedDiscount(discount);
    setShowDiscountInfoModal(true);
  };

  return {
    discounts,
    showDiscountModal,
    showDiscountInfoModal,
    showEditDiscountModal,
    editingDiscount,
    selectedDiscount,
    setSelectedDiscount,
    handleAddDiscount,
    handleEditDiscount,
    handleDeleteDiscount,
    handleSubmitDiscount,
    handleSubmitEditDiscount,
    handleViewDiscount,
    setShowDiscountModal,
    setShowDiscountInfoModal,
    setShowEditDiscountModal,
    setEditingDiscount,
  };
};
